import { useEffect, useState } from 'react';
import {
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Alert,
  Typography,
} from '@mui/material';

import { Box } from '@mui/system';
import DynamicModal from '@components/DynamicModal/DynamicModal';
import UserDetailRow from '@features/employerAccount/components/UserDetailRow/UserDetailRow';
import { white } from '@styles/colors';
import { useEditTemplate } from '../hooks/useEditTemplate';
import { EditIcon } from '../../../vectors/index';
import { isValidHttpUrl } from '@utils/validHttpUrl';

export default function TemplateInterviewSettings({ templateData }: any) {
  const [defaultType, setDefaultType] = useState<'inPerson' | 'virtual'>(
    'inPerson',
  );

  const [virtualLocation, setVirtualLocation] = useState('');
  const [openDefaultInterview, setOpenDefaultInterview] = useState(false);
  const [physicalLocation, setPhysicalLocation] = useState('');
  const [isVirtualInterviewModal, setIsVirtualInterviewModal] = useState(false);
  const [errorText, setErrorText] = useState('');

  const { editTemplate, isUpdatingTemplate } = useEditTemplate({
    setErrorText,
  });

  function handleSave() {
    editTemplate({
      template_id: templateData?.id,
      physical_interview_address: defaultType === 'inPerson' ? 'true' : 'false',
      virtual_interview_link: virtualLocation || '',
    });
  }

  function handleVirtualLinkSave() {
    if (virtualLocation && isValidHttpUrl(virtualLocation)) {
      editTemplate({
        template_id: templateData?.id,
        virtual_interview_link: virtualLocation || '',
      });
    }
  }

  const physical_address_concate =
    templateData &&
    templateData?.additional_info &&
    templateData?.additional_info?.physical_interview_location
      ? templateData?.additional_info?.physical_interview_location
      : templateData
        ? `${templateData?.company?.s_address1 || templateData?.company?.s_address2 || ''} ${templateData?.company?.s_city || ''} ${templateData?.company?.s_state || ''} ${templateData?.company?.s_zip || ''}`.trim()
        : '';

  useEffect(() => {
    if (templateData?.additional_info) {
      setPhysicalLocation(
        physical_address_concate ? physical_address_concate : '',
      );
      setVirtualLocation(
        templateData?.additional_info?.virtual_interview_link || '',
      );
      setDefaultType(
        templateData?.additional_info?.physical_interview_address === 'true'
          ? 'inPerson'
          : 'virtual',
      );
    }
  }, [templateData?.additional_info]);

  return (
    <Stack bgcolor={white} p={3} borderRadius={8} width="100%">
      {errorText && <Alert severity="error">{errorText}</Alert>}

      <UserDetailRow
        label="Default Interview Type"
        value={defaultType}
        action={() => setOpenDefaultInterview(true)}
        isEdit={true}
      />

      <UserDetailRow
        label="Virtual Interview Location:"
        value={virtualLocation || ''}
        action={() => setIsVirtualInterviewModal(true)}
        isEdit={true}
      />

      {/* .....Default interview type modal... */}
      <DynamicModal
        open={openDefaultInterview}
        onClose={() => {
          setOpenDefaultInterview(false);

          setDefaultType(
            templateData?.additional_info?.physical_interview_address ===
              'true' ||
              templateData?.additional_info?.physical_interview_address ===
                undefined
              ? 'inPerson'
              : 'virtual',
          );
        }}
        title="Default Interview Type"
        onSave={handleSave}
        loading={isUpdatingTemplate}
        disabled={
          defaultType === 'virtual' &&
          !templateData?.additional_info?.virtual_interview_link
            ? true
            : false
        }
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <RadioGroup
            row
            value={defaultType}
            onChange={(e) =>
              setDefaultType(e.target.value as 'inPerson' | 'virtual')
            }
            sx={{ justifyContent: 'center', gap: '20px' }}
          >
            {/* Physical Radio */}
            <FormControlLabel
              value="inPerson"
              control={<Radio />}
              label="In Person"
            />

            {/* Virtual Radio */}
            <FormControlLabel
              value="virtual"
              control={<Radio />}
              label="Virtual"
            />
          </RadioGroup>
          {defaultType === 'virtual' &&
            !templateData?.additional_info?.virtual_interview_link && (
              <Typography
                variant="body2"
                color="red"
                fontSize="15px"
                sx={{ pl: 2, py: 2 }}
              >
                Please set a virtual interview link.
              </Typography>
            )}
        </Box>
      </DynamicModal>

      {/* .....Virtual link Modal... */}
      <DynamicModal
        open={isVirtualInterviewModal}
        onClose={() => {
          setIsVirtualInterviewModal(false);
          setVirtualLocation(
            templateData?.additional_info
              ? templateData?.additional_info?.virtual_interview_link
              : virtualLocation,
          );
        }}
        title="Enter Your Link"
        onSave={handleVirtualLinkSave}
        icon={<EditIcon />}
        loading={isUpdatingTemplate}
      >
        <TextField
          placeholder="Enter meeting link"
          value={virtualLocation}
          onChange={(e: any) => setVirtualLocation(e.target.value)}
          fullWidth
          error={
            (virtualLocation !== '' && !isValidHttpUrl(virtualLocation)) ||
            virtualLocation === ''
          }
          helperText={
            virtualLocation === ''
              ? 'Meeting link is required'
              : !isValidHttpUrl(virtualLocation)
                ? 'Invalid URL'
                : ''
          }
        />
      </DynamicModal>
    </Stack>
  );
}